import { type DataLabel, dataLabel } from '@/i18n/dataLabels';
import type { FishTypeInfo } from '@/services/types';

/**
 * Just the part of a pen these read. Structural on purpose: an `OverviewCage` satisfies it, and
 * so does a fixture that carries only a species.
 */
interface PenSpecies {
    fishType: FishTypeInfo | null;
}

export interface OverviewSpecies {
    /** The species when every pen has one and they all agree, null otherwise. */
    single: FishTypeInfo | null;
    /** The distinct species present, in first-seen order. Excludes pens with none. */
    present: FishTypeInfo[];
}

/**
 * What a location can honestly say about species, given its pens.
 *
 * `locationFishType` is only the default for new pens, so reporting it as the location's
 * species is false the moment two pens differ (WB-2207). Both values here come from ONE pen
 * array on purpose: computing them from different sets is how a caller ends up showing a
 * single species beside a breakdown that contradicts it.
 *
 * A pen with no species makes `single` null even when every pen that has one agrees, matching
 * the backend summariser: an unknown pen has not been shown to agree with the rest. It is
 * absent from `present`, so a null `single` beside a one-entry `present` is exactly that case.
 *
 * Nothing here names a species, so a species added to the database flows through untouched.
 */
export function summariseOverviewSpecies(cages: PenSpecies[]): OverviewSpecies {
    const present: FishTypeInfo[] = [];
    let unknown = 0;

    for (const cage of cages) {
        const fishType = cage.fishType;
        if (!fishType) {
            unknown += 1;
            continue;
        }
        if (!present.some((seen) => seen.shortName === fishType.shortName)) {
            present.push(fishType);
        }
    }

    return {
        single: unknown === 0 && present.length === 1 ? present[0] : null,
        present,
    };
}

const SEVERAL_SPECIES = 'fishType.several';

/**
 * The translation key for a location's species label, or null when there is nothing to show.
 *
 * Species labels live under one key space, `common.fishTypes`, keyed on the stored
 * `fishTypes.name`. The stored name is the fallback, so a species inserted into the database
 * shows its name until someone translates it, and never a missing-key string. `several` is not
 * a species and has a key of its own.
 */
export function speciesLabelKey(
    species: OverviewSpecies,
    locationDefault: FishTypeInfo | null
): DataLabel | null {
    if (species.present.length > 1) {
        return { key: SEVERAL_SPECIES, defaultValue: SEVERAL_SPECIES };
    }

    const name = (species.single ?? locationDefault)?.name;
    return name ? dataLabel('fishTypes', name) : null;
}
